import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faClipboardList,
  faFilterCircleXmark,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { SkeletonTable } from '@/components/skeletons';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  fetchEmailDeliveryLogs,
  toDeliveryRange,
  type EmailDeliveryLogEntry,
  type EmailDeliveryStatus,
} from './communication/email-delivery-log.api';

/**
 * Phase 12.7b Wave 2 — Settings → Communication → Email Delivery Log.
 *
 * Design of record: `docs/EMAIL_DELIVERY_LOG_DESIGN.md` · governance **D-123**.
 *
 * ── WHAT THIS PAGE IS NOT ──────────────────────────────────────────────────
 * Not a queue view: email delivery is synchronous and nothing is pending
 * (OD-1a). **Not an action surface: there is no retry, resend, delete or
 * export** (OD-1b) — the page has zero mutations. Not a message viewer: the
 * email body is never stored and never returned (OD-1c).
 *
 * ── THE HONESTY THIS PAGE OWES THE OPERATOR ────────────────────────────────
 * `SENT` means the gateway ACCEPTED the message. It is not proof of inbox
 * delivery: no bounce or webhook processing exists in this product, so a
 * message accepted here can still be rejected downstream. The page says so in
 * words rather than letting a green tick imply more than it can.
 */

const PAGE_SIZES = [20, 50, 100] as const;
type LoadState = 'loading' | 'ready' | 'error';

const PURPOSE_LABELS: Record<string, string> = {
  'password-reset': 'Password reset',
  'customer-password-reset': 'Customer password reset',
  'template-test': 'Template test',
  unknown: 'Unspecified',
};

const purposeLabel = (p: string): string =>
  PURPOSE_LABELS[p] ?? p.replace(/[-_]+/g, ' ').replace(/^./, (c) => c.toUpperCase());

export function EmailDeliveryLogPage() {
  const [entries, setEntries] = useState<EmailDeliveryLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [retentionDays, setRetentionDays] = useState(0);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const [status, setStatus] = useState<EmailDeliveryStatus | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const hasFilters = status !== '' || fromDate !== '' || toDate !== '';

  const clearFilters = useCallback(() => {
    setStatus('');
    setFromDate('');
    setToDate('');
    setOffset(0);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    setExpandedId(null);

    const range = toDeliveryRange(fromDate, toDate);
    fetchEmailDeliveryLogs(
      {
        limit: pageSize,
        offset,
        ...(status ? { status } : {}),
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
      },
      controller.signal,
    )
      .then((page) => {
        if (controller.signal.aborted) return;
        setEntries(page.items);
        setTotal(page.total);
        setRetentionDays(page.retentionDays);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // An outage must never render as "no email was sent" — that inverts the
        // page's meaning entirely.
        setErrorMessage(getApiErrorMessage(error, 'Unable to load email delivery records.'));
        setEntries([]);
        setTotal(0);
        setState('error');
      });

    return () => controller.abort();
  }, [status, fromDate, toDate, pageSize, offset, reloadToken]);

  const rangeLabel = useMemo(() => {
    if (total === 0 || entries.length === 0) return null;
    return `Showing ${(offset + 1).toLocaleString()}–${(offset + entries.length).toLocaleString()} of ${total.toLocaleString()}`;
  }, [offset, entries.length, total]);

  const canPrev = offset > 0;
  const canNext = offset + entries.length < total;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Email Delivery Log</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          A record of outgoing email delivery attempts. Read-only.
        </p>
      </div>

      {/* ── Filters — exactly two: status and date range ─────────────────── */}
      <section
        aria-label="Email delivery filters"
        className="rounded-xl border border-border bg-card p-5"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <label
              htmlFor="edl-status"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Status
            </label>
            <select
              id="edl-status"
              aria-label="Filter by delivery status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as EmailDeliveryStatus | '');
                setOffset(0);
              }}
              className="h-11 w-full min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">All statuses</option>
              <option value="SENT">Sent</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>

          <div className="min-w-0">
            <label
              htmlFor="edl-from"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              From
            </label>
            <input
              id="edl-from"
              type="date"
              aria-label="Filter from date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setOffset(0);
              }}
              className="h-11 w-full min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>

          <div className="min-w-0">
            <label
              htmlFor="edl-to"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              To
            </label>
            <input
              id="edl-to"
              type="date"
              aria-label="Filter to date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setOffset(0);
              }}
              className="h-11 w-full min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>

          <div className="flex min-w-0 items-end">
            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasFilters}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <FontAwesomeIcon icon={faFilterCircleXmark} className="h-3.5 w-3.5" />
              Clear filters
            </button>
          </div>
        </div>
      </section>

      {/* ── Results ─────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {state === 'loading' ? 'Loading delivery records…' : (rangeLabel ?? 'No records')}
          </p>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <label htmlFor="edl-size">Per page</label>
            <select
              id="edl-size"
              aria-label="Records per page"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setOffset(0);
              }}
              className="h-11 rounded-lg border border-border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-5">
          {state === 'loading' ? (
            <div data-testid="edl-loading">
              <SkeletonTable rows={6} cols={5} />
            </div>
          ) : state === 'error' ? (
            <Message
              testId="edl-error"
              icon={faTriangleExclamation}
              message={errorMessage}
              actionLabel="Retry"
              onAction={() => setReloadToken((t) => t + 1)}
            />
          ) : entries.length === 0 ? (
            hasFilters ? (
              <Message
                testId="edl-no-results"
                icon={faClipboardList}
                message="No delivery records match your filters."
                actionLabel="Clear filters"
                onAction={clearFilters}
              />
            ) : (
              <Message
                testId="edl-empty"
                icon={faClipboardList}
                message="No email delivery activity recorded yet."
              />
            )
          ) : (
            <Results
              entries={entries}
              expandedId={expandedId}
              onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
            disabled={!canPrev || state === 'loading'}
            className="inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setOffset((o) => o + pageSize)}
            disabled={!canNext || state === 'loading'}
            className="inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Next
          </button>
        </div>
      </section>

      {/* ── The two limits this page must state, not imply ───────────────── */}
      <p className="text-xs text-muted-foreground" data-testid="edl-scope-note">
        <strong>Sent</strong> means the gateway accepted the message; it is not confirmation that it
        reached the recipient&apos;s inbox, and bounces are not tracked. Records are retained for{' '}
        {retentionDays} days and then deleted. Email content is never stored.
      </p>
    </div>
  );
}

function Message({
  icon,
  message,
  onAction,
  actionLabel,
  testId,
}: {
  icon: typeof faClipboardList;
  message: string;
  onAction?: () => void;
  actionLabel?: string;
  testId: string;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center px-4 py-12 text-center"
      data-testid={testId}
      role="status"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
        <FontAwesomeIcon icon={icon} className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="mt-3 max-w-sm text-sm text-muted-foreground">{message}</p>
      {onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {actionLabel ?? 'Retry'}
        </button>
      )}
    </div>
  );
}

/** One dataset, two presentations — table on desktop, stacked cards below `md`. */
function Results({
  entries,
  expandedId,
  onToggle,
}: {
  entries: EmailDeliveryLogEntry[];
  expandedId: string | null;
  onToggle: (id: string) => void;
}) {
  const when = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  return (
    <>
      <div className="hidden md:block">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">Email delivery attempts, newest first</caption>
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="w-8 px-2 py-2" />
              <th scope="col" className="px-2 py-2 font-medium">
                Sent At
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Recipient
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Purpose
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Status
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Error
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const expanded = expandedId === e.id;
              return (
                <Fragment key={e.id}>
                  <tr className="border-b border-border last:border-0">
                    <td className="px-2 py-3 align-top">
                      <button
                        type="button"
                        onClick={() => onToggle(e.id)}
                        aria-expanded={expanded}
                        aria-controls={`edl-detail-${e.id}`}
                        aria-label={`${expanded ? 'Hide' : 'Show'} details for ${e.recipient}`}
                        data-testid={`edl-expand-${e.id}`}
                        className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <FontAwesomeIcon
                          icon={expanded ? faChevronDown : faChevronRight}
                          className="h-3 w-3"
                        />
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-2 py-3 align-top text-foreground">
                      {when(e.occurredAt)}
                    </td>
                    <td className="max-w-[16rem] truncate px-2 py-3 align-top text-foreground">
                      {e.recipient}
                    </td>
                    <td className="px-2 py-3 align-top text-muted-foreground">
                      {purposeLabel(e.purpose)}
                    </td>
                    <td className="px-2 py-3 align-top">
                      <StatusBadge status={e.status} />
                    </td>
                    <td className="max-w-[16rem] truncate px-2 py-3 align-top text-muted-foreground">
                      {e.errorMessage ?? '—'}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="border-b border-border last:border-0">
                      <td colSpan={6} className="px-2 pb-4">
                        <Detail entry={e} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul className="grid grid-cols-1 gap-3 md:hidden" data-testid="edl-cards">
        {entries.map((e) => {
          const expanded = expandedId === e.id;
          return (
            <li key={e.id} className="min-w-0 rounded-lg border border-border p-4">
              <button
                type="button"
                onClick={() => onToggle(e.id)}
                aria-expanded={expanded}
                aria-controls={`edl-detail-card-${e.id}`}
                className="flex w-full min-w-0 items-start justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className="min-w-0">
                  <span className="block break-words text-sm font-semibold text-foreground">
                    {e.recipient}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {when(e.occurredAt)}
                  </span>
                </span>
                <FontAwesomeIcon
                  icon={expanded ? faChevronDown : faChevronRight}
                  className="mt-1 h-3 w-3 shrink-0 text-muted-foreground"
                />
              </button>
              <dl className="mt-3 grid grid-cols-1 gap-1.5 text-xs">
                <Field label="Purpose" value={purposeLabel(e.purpose)} />
                <Field label="Status" value={e.status === 'SENT' ? 'Sent' : 'Failed'} />
                <Field label="Error" value={e.errorMessage ?? '—'} />
              </dl>
              {expanded && (
                <div className="mt-3" id={`edl-detail-card-${e.id}`}>
                  <Detail entry={e} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** Colour is never the sole indicator — the word is always present. */
function StatusBadge({ status }: { status: EmailDeliveryStatus }) {
  const sent = status === 'SENT';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
        sent
          ? 'border-emerald-600/30 text-emerald-700 dark:text-emerald-400'
          : 'border-red-600/30 text-red-700 dark:text-red-400'
      }`}
    >
      {sent ? 'Sent' : 'Failed'}
    </span>
  );
}

/** Operational metadata only — no body, no subject, no payload exists to show. */
function Detail({ entry }: { entry: EmailDeliveryLogEntry }) {
  return (
    <div
      id={`edl-detail-${entry.id}`}
      className="rounded-lg bg-muted/40 p-4"
      data-testid={`edl-detail-${entry.id}`}
    >
      <dl className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        <Field label="Record ID" value={entry.id} mono />
        <Field label="Recipient" value={entry.recipient} />
        <Field label="Exact time" value={new Date(entry.occurredAt).toISOString()} mono />
        <Field label="Gateway scope" value={entry.gatewayScope ?? '—'} />
        <Field label="Gateway origin" value={entry.gatewayOrigin ?? '—'} />
        <Field label="Gateway ID" value={entry.gatewayId ?? '—'} mono />
        <div className="min-w-0 sm:col-span-2">
          <dt className="text-muted-foreground">Failure reason</dt>
          <dd className="break-words font-mono text-[11px] text-foreground">
            {entry.errorMessage ?? '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`break-words text-foreground ${mono ? 'font-mono text-[11px]' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
